
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Button from '../../components/base/Button';
import SEOHead from '../../components/SEO/SEOHead';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const seoData = {
    title: 'گلخانه سبز آریا - پیشرو در ساخت گلخانه‌های هوشمند و مدرن ایران | فناوری هلندی',
    description: 'شرکت گلخانه سبز آریا با بیش از 15 سال تجربه، ارائه‌دهنده گلخانه‌های هوشمند، تجهیزات پیشرفته گلخانه، کوکوپیت، نخ یووی دار، توری ضد تریپس و خدمات مشاوره تخصصی ساخت گلخانه با فناوری هلندی. بیش از 50 پروژه موفق در سراسر ایران.',
    keywords: 'گلخانه هوشمند، ساخت گلخانه، تجهیزات گلخانه، کوکوپیت، نخ یووی دار، توری ضد تریپس، چسب ترمیم پلاستیک، گلخانه هلندی، شرکت آریا، گلخانه شیشه‌ای، کشت هیدروپونیک، پیت ماس، هیتر گلخانه، سیستم آبیاری قطره‌ای، کنترل اقلیم گلخانه، اتوماسیون گلخانه، مشاوره گلخانه‌سازی، طراحی گلخانه، گلخانه مدرن ایران',
    ogImage: 'https://public.readdy.ai/ai/img_res/83d9e5e44e5f958ea505bbb9119e1c13.jpg',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "گلخانه سبز آریا",
      "alternateName": "Aria Green House",
      "description": "شرکت پیشرو در ساخت گلخانه‌های هوشمند و ارائه تجهیزات پیشرفته گلخانه با فناوری هلندی",
      "url": (import.meta.env.VITE_SITE_URL || 'https://example.com'),
      "logo": (import.meta.env.VITE_SITE_URL || 'https://example.com') + "/logo.png",
      "image": "https://readdy.ai/api/search-image?query=Modern%20glass%20greenhouse%20with%20advanced%20hydroponic%20systems%2C%20automated%20climate%20control%2C%20and%20lush%20green%20plants%20growing%20inside%2C%20professional%20agricultural%20technology%2C%20clean%20and%20bright%20interior%20with%20natural%20lighting&width=1200&height=630&seq=org-image&orientation=landscape&format=webp&quality=85",
      "foundingDate": "2009",
      "founder": {
        "@type": "Person",
        "name": "مدیرعامل شرکت آریا"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "۰۲۱۸۸۴۴۱۳۸۶",
        "contactType": "customer service",
        "availableLanguage": ["Persian", "English", "Arabic", "Russian"],
        "areaServed": "IR"
      },
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "IR",
        "addressLocality": "Tehran",
        "addressRegion": "Tehran Province"
      },
      "sameAs": [
        "https://instagram.com/ariagreenhouse",
        "https://telegram.me/ariagreenhouse"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "محصولات و خدمات گلخانه",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "گلخانه‌های هوشمند",
              "description": "طراحی و ساخت گلخانه‌های مدرن با فناوری هلندی"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "تجهیزات گلخانه",
              "description": "کوکوپیت، نخ یووی دار، توری ضد تریپس و سایر تجهیزات"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "مشاوره تخصصی",
              "description": "خدمات مشاوره و طراحی گلخانه"
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "100",
        "bestRating": "5"
      },
      "review": [
        {
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": "احمد رضایی"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5"
          },
          "reviewBody": "کیفیت بالا و خدمات عالی. گلخانه ساخته شده توسط آریا بسیار مدرن و کارآمد است."
        }
      ]
    }
  };

  const heroSlides = [
    {
      title: t('hero.slide1.title'),
      subtitle: t('hero.slide1.subtitle'),
      description: t('hero.slide1.description'),
      image: 'https://readdy.ai/api/search-image?query=Stunning%20large%20scale%20modern%20glass%20greenhouse%20complex%20with%20crystal%20clear%20glass%20panels%2C%20advanced%20automated%20systems%20visible%20inside%2C%20lush%20green%20plants%20growing%20in%20perfect%20rows%2C%20dramatic%20lighting%20with%20golden%20hour%20sunlight%20streaming%20through%20glass%2C%20professional%20agricultural%20technology%2C%20clean%20minimalist%20design%20with%20mountains%20in%20background&width=1920&height=1080&seq=hero-main-webp&orientation=landscape&format=webp&quality=80'
    },
    {
      title: t('hero.slide2.title'),
      subtitle: t('hero.slide2.subtitle'),
      description: t('hero.slide2.description'),
      image: 'https://readdy.ai/api/search-image?query=High-tech%20greenhouse%20interior%20with%20advanced%20hydroponic%20systems%2C%20automated%20climate%20control%20panels%20with%20digital%20displays%2C%20robotic%20irrigation%20systems%2C%20sensors%20and%20monitoring%20equipment%2C%20futuristic%20agricultural%20technology%2C%20bright%20clean%20environment%20with%20perfect%20lighting&width=1920&height=1080&seq=hero-tech-webp&orientation=landscape&format=webp&quality=80'
    },
    {
      title: t('hero.slide3.title'),
      subtitle: t('hero.slide3.subtitle'),
      description: t('hero.slide3.description'),
      image: 'https://readdy.ai/api/search-image?query=Aerial%20panoramic%20view%20of%20multiple%20connected%20glass%20greenhouse%20structures%20across%20Iranian%20landscape%2C%20modern%20agricultural%20complex%20with%20geometric%20glass%20buildings%2C%20surrounded%20by%20green%20fields%20and%20mountains%2C%20professional%20development%20project%2C%20impressive%20scale%20and%20organization&width=1920&height=1080&seq=hero-aerial-webp&orientation=landscape&format=webp&quality=80'
    }
  ];

  const services = [
    {
      icon: 'ri-building-line',
      title: t('services.design.title'),
      description: t('services.design.description')
    },
    {
      icon: 'ri-settings-3-line',
      title: t('services.smart.title'),
      description: t('services.smart.description')
    },
    {
      icon: 'ri-customer-service-2-line',
      title: t('services.consulting.title'),
      description: t('services.consulting.description')
    },
    {
      icon: 'ri-tools-line',
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description')
    }
  ];

  const stats = [
    { number: '50+', label: t('stats.projects'), icon: 'ri-building-line' },
    { number: '150,000+', label: t('stats.area'), icon: 'ri-ruler-line' },
    { number: '15+', label: t('stats.experience'), icon: 'ri-time-line' },
    { number: '100+', label: t('stats.customers'), icon: 'ri-user-smile-line' }
  ];

  const testimonials = [
    {
      name: t('testimonials.customer1.name'),
      position: t('testimonials.customer1.position'),
      text: t('testimonials.customer1.text'),
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=Professional%20middle-aged%20Iranian%20businessman%20in%20agricultural%20setting%2C%20wearing%20business%20casual%20attire%2C%20confident%20smile%2C%20greenhouse%20background%20with%20plants&width=100&height=100&seq=testimonial1&orientation=squarish&format=webp&quality=85'
    },
    {
      name: t('testimonials.customer2.name'),
      position: t('testimonials.customer2.position'),
      text: t('testimonials.customer2.text'),
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=Professional%20Iranian%20woman%20agricultural%20engineer%20wearing%20hijab%2C%20standing%20in%20modern%20greenhouse%2C%20holding%20tablet%2C%20confident%20and%20knowledgeable%20expression&width=100&height=100&seq=testimonial2&orientation=squarish&format=webp&quality=85'
    },
    {
      name: t('testimonials.customer3.name'),
      position: t('testimonials.customer3.position'),
      text: t('testimonials.customer3.text'),
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=Successful%20Iranian%20greenhouse%20owner%20in%20his%2040s%2C%20standing%20proudly%20in%20front%20of%20his%20glass%20greenhouse%20facility%2C%20wearing%20work%20clothes%2C%20satisfied%20expression&width=100&height=100&seq=testimonial3&orientation=squarish&format=webp&quality=85'
    }
  ];

  const partners = [
    {
      name: t('partners.vander.name'),
      logo: 'https://readdy.ai/api/search-image?query=Dutch%20agricultural%20technology%20company%20logo%20design%2C%20professional%20corporate%20branding%2C%20green%20and%20blue%20colors%2C%20modern%20typography%2C%20greenhouse%20industry&width=200&height=100&seq=partner1&orientation=landscape&format=webp&quality=90',
      description: t('partners.vander.description')
    },
    {
      name: t('partners.chehelsotoon.name'),
      logo: 'https://readdy.ai/api/search-image?query=Iranian%20agricultural%20company%20logo%20with%20Persian%20elements%2C%20professional%20design%2C%20green%20colors%2C%20modern%20corporate%20branding%20for%20greenhouse%20industry&width=200&height=100&seq=partner2&orientation=landscape&format=webp&quality=90',
      description: t('partners.chehelsotoon.description')
    }
  ];

  useEffect(() => {
    // Preload the first hero image for better performance
    const firstImage = new Image();
    firstImage.src = heroSlides[0].image;
    firstImage.loading = 'eager';
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className="min-h-screen">
      <SEOHead {...seoData} />
      <Header />
      
      {/* Hero Section - بهبود یافته */}
      <section className="relative h-screen overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1500 ease-in-out ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[6000ms] ease-out"
              style={{ 
                backgroundImage: `url(${slide.image})`,
                transform: index === currentSlide ? 'scale(1.05)' : 'scale(1)',
                ...(index === 0 && { 
                  backgroundImage: `url(${slide.image})`,
                  imageRendering: 'crisp-edges',
                  willChange: 'transform'
                })
              }}
            >
              {/* Preload hint for the first image */}
              {index === 0 && (
                <link 
                  rel="preload" 
                  as="image" 
                  href={slide.image}
                  fetchPriority="high"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/60"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 via-transparent to-teal-900/10"></div>
            </div>
            
            <div className="relative h-full flex items-center justify-center">
              <div className="w-full px-4 lg:px-8 pt-20">
                <div className="max-w-5xl mx-auto text-center text-white">
                  <div className={`transform transition-all duration-1000 delay-300 ${
                    index === currentSlide && isLoaded 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-8 opacity-0'
                  }`}>
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                      <span className="bg-gradient-to-r from-white via-emerald-100 to-teal-100 bg-clip-text text-transparent drop-shadow-2xl">
                        {slide.title}
                      </span>
                    </h1>
                  </div>
                  
                  <div className={`transform transition-all duration-1000 delay-500 ${
                    index === currentSlide && isLoaded 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-8 opacity-0'
                  }`}>
                    <p className="text-xl md:text-2xl lg:text-3xl mb-4 text-emerald-300 font-semibold drop-shadow-lg">
                      {slide.subtitle}
                    </p>
                  </div>
                  
                  <div className={`transform transition-all duration-1000 delay-700 ${
                    index === currentSlide && isLoaded 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-8 opacity-0'
                  }`}>
                    <p className="text-lg md:text-xl lg:text-2xl mb-10 leading-relaxed max-w-4xl mx-auto text-gray-100 drop-shadow-md">
                      {slide.description}
                    </p>
                  </div>
                  
                  <div className={`transform transition-all duration-1000 delay-900 ${
                    index === currentSlide && isLoaded 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-8 opacity-0'
                  }`}>
                    <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center items-center">
                      <Link
                        to="/products"
                        className="group bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-700 text-white px-8 lg:px-10 py-4 lg:py-5 rounded-full font-bold text-lg lg:text-xl transition-all duration-300 shadow-2xl hover:shadow-emerald-500/25 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:-translate-y-1"
                      >
                        <span className="flex items-center space-x-3">
                          <i className="ri-product-hunt-line text-xl"></i>
                          <span>{t('buttons.view_products')}</span>
                          <i className="ri-arrow-left-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
                        </span>
                      </Link>
                      
                      <Link
                        to="/contact"
                        className="group bg-gradient-to-r from-white/20 to-white/10 backdrop-blur-sm border-2 border-white/50 text-white hover:bg-white hover:text-emerald-700 px-8 lg:px-10 py-4 lg:py-5 rounded-full font-bold text-lg lg:text-xl transition-all duration-300 shadow-2xl hover:shadow-white/25 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:-translate-y-1"
                      >
                        <span className="flex items-center space-x-3">
                          <i className="ri-customer-service-2-line text-xl"></i>
                          <span>{t('buttons.request_consultation')}</span>
                          <i className="ri-phone-line text-lg group-hover:rotate-12 transition-transform duration-300"></i>
                        </span>
                      </Link>
                      
                      <Link
                        to="/projects"
                        className="group border-2 border-emerald-400/80 text-emerald-300 hover:bg-gradient-to-r hover:from-emerald-600 hover:to-teal-600 hover:text-white hover:border-transparent px-8 lg:px-10 py-4 lg:py-5 rounded-full font-bold text-lg lg:text-xl transition-all duration-300 shadow-2xl hover:shadow-emerald-500/25 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:-translate-y-1"
                      >
                        <span className="flex items-center space-x-3">
                          <i className="ri-building-2-line text-xl"></i>
                          <span>{t('buttons.view_projects')}</span>
                          <i className="ri-external-link-line text-lg group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"></i>
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Slide Indicators - بهبود یافته */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`relative w-4 h-4 rounded-full transition-all duration-500 cursor-pointer group ${
                index === currentSlide 
                  ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50 scale-125' 
                  : 'bg-white/50 hover:bg-white/80 hover:scale-110'
              }`}
            >
              {index === currentSlide && (
                <div className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75"></div>
              )}
            </button>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-emerald-500/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-1/3 right-16 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/4 w-16 h-16 bg-cyan-500/10 rounded-full blur-lg animate-pulse delay-2000"></div>
      </section>

      {/* Stats Section - Enhanced Professional Design */}
      <section className="relative py-24 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
          <div className="absolute top-20 right-20 w-40 h-40 bg-teal-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-1/4 w-24 h-24 bg-cyan-500/5 rounded-full blur-2xl"></div>
          <div className="absolute bottom-20 right-1/3 w-36 h-36 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-3 bg-white/80 backdrop-blur-sm border border-emerald-200/50 px-6 py-3 rounded-full mb-6 shadow-lg">
                <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-emerald-700 font-semibold text-sm">دستاوردهای ما</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 via-gray-700 to-slate-600 bg-clip-text text-transparent mb-4">
                آمار و ارقام
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                نگاهی به دستاوردهای ما در صنعت گلخانه‌سازی ایران
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Stat 1 - Projects */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
                <div className="relative bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-emerald-100/50 transition-all duration-500 hover:-translate-y-2 text-center">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl group-hover:shadow-emerald-500/25 transition-all duration-300 group-hover:scale-110">
                      <i className="ri-building-line text-white text-3xl"></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <i className="ri-star-fill text-white text-xs"></i>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent group-hover:from-emerald-700 group-hover:to-teal-700 transition-all duration-300">
                      50+
                    </h3>
                    <p className="text-gray-700 font-semibold text-lg">پروژه موفق</p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      پروژه‌های گلخانه‌ای در سراسر کشور
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-6 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full w-0 group-hover:w-full transition-all duration-1000 delay-300"></div>
                  </div>
                </div>
              </div>

              {/* Stat 2 - Area */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
                <div className="relative bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-teal-100/50 transition-all duration-500 hover:-translate-y-2 text-center">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl group-hover:shadow-teal-500/25 transition-all duration-300 group-hover:scale-110">
                      <i className="ri-ruler-line text-white text-3xl"></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                      <i className="ri-trophy-fill text-white text-xs"></i>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent group-hover:from-teal-700 group-hover:to-cyan-700 transition-all duration-300">
                      150K+
                    </h3>
                    <p className="text-gray-700 font-semibold text-lg">متر مربع ساخت</p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      مساحت کل گلخانه‌های ساخته شده
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-6 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-500 to-cyan-500 h-full rounded-full w-0 group-hover:w-full transition-all duration-1000 delay-300"></div>
                  </div>
                </div>
              </div>

              {/* Stat 3 - Experience */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
                <div className="relative bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-cyan-100/50 transition-all duration-500 hover:-translate-y-2 text-center">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl group-hover:shadow-cyan-500/25 transition-all duration-300 group-hover:scale-110">
                      <i className="ri-time-line text-white text-3xl"></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                      <i className="ri-award-fill text-white text-xs"></i>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent group-hover:from-cyan-700 group-hover:to-blue-700 transition-all duration-300">
                      15+
                    </h3>
                    <p className="text-gray-700 font-semibold text-lg">سال تجربه</p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      سابقه فعالیت در صنعت گلخانه‌سازی
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-6 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full rounded-full w-0 group-hover:w-full transition-all duration-1000 delay-300"></div>
                  </div>
                </div>
              </div>

              {/* Stat 4 - Customers */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
                <div className="relative bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-blue-100/50 transition-all duration-500 hover:-translate-y-2 text-center">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl group-hover:shadow-blue-500/25 transition-all duration-300 group-hover:scale-110">
                      <i className="ri-user-smile-line text-white text-3xl"></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <i className="ri-heart-fill text-white text-xs"></i>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent group-hover:from-blue-700 group-hover:to-indigo-700 transition-all duration-300">
                      100+
                    </h3>
                    <p className="text-gray-700 font-semibold text-lg">مشتری راضی</p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      مشتریان وفادار در سراسر کشور
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-6 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full rounded-full w-0 group-hover:w-full transition-all duration-1000 delay-300"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Achievement Section */}
            <div className="mt-16 bg-gradient-to-r from-slate-800 via-gray-800 to-slate-800 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20"></div>
                <div className="absolute top-10 left-10 w-20 h-20 border border-white/20 rounded-full"></div>
                <div className="absolute top-20 right-20 w-16 h-16 border border-white/20 rounded-full"></div>
                <div className="absolute bottom-10 left-1/4 w-12 h-12 border border-white/20 rounded-full"></div>
                <div className="absolute bottom-20 right-1/3 w-24 h-24 border border-white/20 rounded-full"></div>
              </div>

              <div className="relative text-center">
                <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-full mb-6">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                  <span className="text-yellow-300 font-semibold">افتخارات ما</span>
                </div>
                
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  پیشرو در صنعت گلخانه‌سازی ایران
                </h3>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                  با بیش از یک دهه تجربه، ما به عنوان یکی از معتبرترین شرکت‌های گلخانه‌سازی کشور شناخته می‌شویم
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-medal-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold mb-2">گواهینامه ISO</h4>
                    <p className="text-gray-300 text-sm">استاندارد بین‌المللی کیفیت</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-shield-check-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold mb-2">ضمانت ۵ ساله</h4>
                    <p className="text-gray-300 text-sm">پشتیبانی کامل محصولات</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-customer-service-2-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold mb-2">پشتیبانی ۲۴/۷</h4>
                    <p className="text-gray-300 text-sm">خدمات مشاوره تخصصی</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-white via-gray-50 to-slate-50">
        <div className="px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-800 to-gray-700 bg-clip-text text-transparent mb-6">
                {t('services.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('services.description')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 hover:shadow-xl hover:shadow-emerald-100/50 transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-emerald-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section - بهبود یافته با کارت‌ها */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
        <div className="px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-800 to-gray-700 bg-clip-text text-transparent mb-6">
                {t('about.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('about.description1')}
              </p>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {/* چرا ما؟ */}
              <div className="group bg-gradient-to-br from-white via-gray-50 to-slate-50 border border-gray-200/50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-emerald-100/50 transition-all duration-500 hover:-translate-y-2 transform opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 via-teal-50 to-cyan-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className="ri-award-line text-3xl text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                </div>
                
                <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-emerald-700 transition-colors duration-300">
                  {t('about.why_us.title')}
                </h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.why_us.experience')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.why_us.quality')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.why_us.support')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.why_us.projects')}</span>
                  </div>
                </div>
                
                <p className="text-gray-600 leading-relaxed text-sm mb-6">
                  {t('about.why_us.description')}
                </p>
                
                <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-6"></div>
                
                <Link
                  to="/about"
                  className="group/btn inline-flex items-center space-x-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-all duration-300 cursor-pointer"
                >
                  <span className="text-sm">{t('nav.read_more')}</span>
                  <i className="ri-arrow-left-line text-sm group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                </Link>
              </div>

              {/* تخصص ما */}
              <div className="group bg-gradient-to-br from-white via-gray-50 to-slate-50 border border-gray-200/50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-teal-100/50 transition-all duration-500 hover:-translate-y-2 transform opacity-0 animate-[fadeInUp_0.8s_ease-out_0.4s_forwards]">
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-100 via-cyan-50 to-blue-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className="ri-settings-3-line text-3xl text-teal-600 group-hover:text-teal-700 transition-colors duration-300"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                </div>
                
                <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-teal-700 transition-colors duration-300">
                  {t('about.expertise.title')}
                </h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.expertise.smart_design')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.expertise.climate_control')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.expertise.irrigation')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.expertise.consulting')}</span>
                  </div>
                </div>
                
                <p className="text-gray-600 leading-relaxed text-sm mb-6">
                  {t('about.expertise.description')}
                </p>
                
                <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-6"></div>
                
                <Link
                  to="/services"
                  className="group/btn inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-semibold transition-all duration-300 cursor-pointer"
                >
                  <span className="text-sm">{t('nav.view_services')}</span>
                  <i className="ri-arrow-left-line text-sm group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                </Link>
              </div>

              {/* شراکت بین‌المللی */}
              <div className="group bg-gradient-to-br from-white via-gray-50 to-slate-50 border border-gray-200/50 rounded-3xl p-8 hover:shadow-2xl hover:shadow-cyan-100/50 transition-all duration-500 hover:-translate-y-2 transform opacity-0 animate-[fadeInUp_0.8s_ease-out_0.6s_forwards]">
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-100 via-blue-50 to-indigo-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className="ri-global-line text-3xl text-cyan-600 group-hover:text-cyan-700 transition-colors duration-300"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                </div>
                
                <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-cyan-700 transition-colors duration-300">
                  {t('about.partnership.title')}
                </h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.partnership.dutch_tech')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.partnership.vander_hoeven')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.partnership.european_standards')}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <div className="w-2 h-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium">{t('about.partnership.global_quality')}</span>
                  </div>
                </div>
                
                <p className="text-gray-600 leading-relaxed text-sm mb-6">
                  {t('about.partnership.description')}
                </p>
                
                <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-6"></div>
                
                <Link
                  to="/about"
                  className="group/btn inline-flex items-center space-x-2 text-cyan-600 hover:text-cyan-700 font-semibold transition-all duration-300 cursor-pointer"
                >
                  <span className="text-sm">{t('nav.view_partners')}</span>
                  <i className="ri-arrow-left-line text-sm group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                </Link>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="text-center transform opacity-0 animate-[fadeInUp_0.8s_ease-out_0.8s_forwards]">
              <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-3xl p-8 text-white shadow-2xl">
                <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                  {t('about.cta.title')}
                </h3>
                <p className="text-lg text-emerald-100 mb-6 max-w-2xl mx-auto">
                  {t('about.cta.description')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/about"
                    className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-information-line ml-2"></i>
                    {t('about.cta.learn_more')}
                  </Link>
                  <Link
                    to="/contact"
                    className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line ml-2"></i>
                    {t('about.cta.contact')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section - طراحی حرفه‌ای */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-emerald-200/30 to-teal-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-r from-cyan-200/30 to-blue-200/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="px-4 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-100 to-teal-100 px-6 py-2 rounded-full mb-6">
                <i className="ri-global-line text-emerald-600"></i>
                <span className="text-emerald-700 font-medium text-sm">{t('partners.international')}</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-800 via-gray-700 to-slate-600 bg-clip-text text-transparent mb-6">
                {t('partners.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {t('partners.description')}
              </p>
            </div>

            {/* Partners Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {/* Partner 1 - Vander Hoeven */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
                <div className="relative bg-white/80 backdrop-blur-sm border border-white/50 rounded-3xl p-10 text-center hover:shadow-2xl hover:shadow-emerald-100/50 transition-all duration-500 group-hover:transform group-hover:scale-105">
                  {/* Partner Logo */}
                  <div className="relative mb-8">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all duration-300">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Dutch%20agricultural%20technology%20company%20logo%20design%2C%20professional%20corporate%20branding%2C%20green%20and%20blue%20colors%2C%20modern%20typography%2C%20greenhouse%20industry&width=200&height=100&seq=partner1&orientation=landscape&format=webp&quality=90"
                        alt="Vander Hoeven"
                        className="h-16 object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-verified-badge-fill text-white text-xs"></i>
                    </div>
                  </div>
                  
                  {/* Partner Info */}
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">{t('partners.vander.name')}</h3>
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 py-2 rounded-full mb-4">
                    <span className="text-lg">🇳🇱</span>
                    <span className="text-blue-700 font-medium text-sm">{t('partners.vander.country')}</span>
                  </div>
                  
                  {/* Partnership Details */}
                  <div className="space-y-3 text-gray-600">
                    <div className="flex items-center justify-center space-x-2">
                      <i className="ri-time-line text-emerald-600"></i>
                      <span className="text-sm">{t('partners.vander.years')}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <i className="ri-award-line text-emerald-600"></i>
                      <span className="text-sm">{t('partners.vander.tech')}</span>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-6">
                    <div className="flex justify-between text-xs text-gray-500 mb-2">
                      <span>{t('nav.cooperation_level')}</span>
                      <span>95%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full" style={{ width: '95%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Partner 2 - Chehelsotoon */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
                <div className="relative bg-white/80 backdrop-blur-sm border border-white/50 rounded-3xl p-10 text-center hover:shadow-2xl hover:shadow-teal-100/50 transition-all duration-500 group-hover:transform group-hover:scale-105">
                  {/* Partner Logo */}
                  <div className="relative mb-8">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all duration-300">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Iranian%20agricultural%20company%20logo%20with%20Persian%20elements%2C%20professional%20design%2C%20green%20colors%2C%20modern%20corporate%20branding%20for%20greenhouse%20industry&width=200&height=100&seq=partner2&orientation=landscape&format=webp&quality=90"
                        alt="Chehelsotoon"
                        className="h-16 object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center">
                      <i className="ri-verified-badge-fill text-white text-xs"></i>
                    </div>
                  </div>
                  
                  {/* Partner Info */}
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">{t('partners.chehelsotoon.name')}</h3>
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 py-2 rounded-full mb-4">
                    <span className="text-lg">🇮🇷</span>
                    <span className="text-green-700 font-medium text-sm">{t('partners.chehelsotoon.country')}</span>
                  </div>
                  
                  {/* Partnership Details */}
                  <div className="space-y-3 text-gray-600">
                    <div className="flex items-center justify-center space-x-2">
                      <i className="ri-time-line text-teal-600"></i>
                      <span className="text-sm">{t('partners.chehelsotoon.years')}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <i className="ri-building-line text-teal-600"></i>
                      <span className="text-sm">{t('partners.chehelsotoon.quality')}</span>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-6">
                    <div className="flex justify-between text-xs text-gray-500 mb-2">
                      <span>{t('nav.cooperation_level')}</span>
                      <span>88%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2 rounded-full" style={{ width: '88%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Partnership Benefits */}
            <div className="bg-gradient-to-r from-white/60 to-gray-50/60 backdrop-blur-sm border border-white/50 rounded-3xl p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-800 mb-4">{t('partners.benefits.title')}</h3>
                <p className="text-gray-600">{t('partners.benefits.description')}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg transition-all duration-300">
                    <i className="ri-global-line text-2xl text-emerald-600"></i>
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-2">{t('partners.benefits.global_access')}</h4>
                  <p className="text-sm text-gray-600">{t('partners.benefits.global_desc')}</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg transition-all duration-300">
                    <i className="ri-lightbulb-line text-2xl text-blue-600"></i>
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-2">{t('partners.benefits.advanced_tech')}</h4>
                  <p className="text-sm text-gray-600">{t('partners.benefits.tech_desc')}</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg transition-all duration-300">
                    <i className="ri-shield-check-line text-2xl text-purple-600"></i>
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-2">{t('partners.benefits.quality_guarantee')}</h4>
                  <p className="text-sm text-gray-600">{t('partners.benefits.quality_desc')}</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg transition-all duration-300">
                    <i className="ri-customer-service-line text-2xl text-orange-600"></i>
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-2">{t('partners.benefits.support')}</h4>
                  <p className="text-sm text-gray-600">{t('partners.benefits.support_desc')}</p>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <div className="inline-flex items-center space-x-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer group">
                <span>{t('partners.cta')}</span>
                <i className="ri-arrow-left-line group-hover:translate-x-1 transition-transform duration-300"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-800 to-gray-700 bg-clip-text text-transparent mb-6">
                {t('testimonials.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('testimonials.description')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover object-top mr-4"
                      loading="lazy"
                    />
                    <div>
                      <h4 className="font-bold text-slate-800">{testimonial.name}</h4>
                      <p className="text-gray-600 text-sm">{testimonial.position}</p>
                    </div>
                  </div>
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    &quot;{testimonial.text}&quot;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600">
        <div className="px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-xl mb-8 leading-relaxed">
              {t('cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-xl whitespace-nowrap cursor-pointer">
                {t('buttons.request_consultation')}
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                {t('cta.phone')}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
